import type { TFunction } from "i18next";
import type { TranslationKeys } from "../../i18n/types";
import { type BreakContribution, EmploymentType, type HoursRateContribution, RateHoursBonusType, type TrainingContribution } from "../../utils/models";
import { getEmploymentTypeDisplayName } from "../../utils/utilFunctions";
import { formatHoursNumber } from "./consts";

/*
 * DAT-906 · Each rule line's scope ("show the match"). Only the fields that RESTRICT the rule are listed,
 * compactly — the point is to tell apart three rules all named "Night", not to reprint the rule.
 */

export const SCOPE_SEPARATOR = " · ";
/** A percentage is stored as a total multiplier (150 = time and a half); people read it as +50%. */
const PERCENT_BASE = 100;
/** Longer lists collapse to "first +N", so a scope stays one glanceable line. */
const MAX_LISTED_SCOPE_VALUES = 2;

type AudienceScope = { roles: string[]; jobIds: string[]; employmentTypes: string[]; union: string[]; isUnion: boolean | null };

/** "18:00:00" → "18:00". The scope's times are the rule's own wall-clock times, not instants. */
const clockOf = (time: string): string => time.split(":").slice(0, 2).join(":");

const isEmploymentType = (value: string): value is EmploymentType => Object.values<string>(EmploymentType).includes(value);
const employmentTypeLabel = (value: string): string => (isEmploymentType(value) ? getEmploymentTypeDisplayName(value) : value);

export const percentOf = (value: number): string => `${formatHoursNumber(value)}%`;

export const compactList = (t: TFunction, values: readonly string[]): string =>
	values.length <= MAX_LISTED_SCOPE_VALUES ? values.join(", ") : t("wageBreakdown.scope.more", { first: values[0], count: values.length - 1 });

/** A tri-state restriction (`true` / `false` / `null` = unrestricted) as its label, or nothing. */
const flagPart = (t: TFunction, value: boolean | null, whenTrue: string, whenFalse: string): string[] =>
	value === true ? [t(whenTrue)] : value === false ? [t(whenFalse)] : [];

const listPart = (t: TFunction, values: readonly string[]): string[] => (values.length > 0 ? [compactList(t, values)] : []);

/** The audience fields every rule kind shares, each only when it narrows the rule. */
export const audienceParts = (t: TFunction, scope: AudienceScope): string[] => [
	...listPart(t, scope.employmentTypes.map(employmentTypeLabel)),
	...flagPart(t, scope.isUnion, "wageBreakdown.scope.union", "wageBreakdown.scope.nonUnion"),
	...listPart(t, scope.union),
	...(scope.jobIds.length > 0 ? [t("wageBreakdown.scope.specificJobs")] : []),
];

export const rolesPart = (t: TFunction, roles: readonly string[]): string[] => listPart(t, roles);

/**
 * An hours-rate rule's match. Roles are always named — "All roles" included — because they are what sets
 * apart same-named rules; the window leads, since it is the rule's reason to exist.
 */
/** "+10", "−10", "+0" — a percentage rule below 100% takes pay away, and must not read "+-10". */
const signedPercentDelta = (delta: number): string => `${delta < 0 ? "−" : "+"}${formatHoursNumber(Math.abs(delta))}`;

export const hoursRateScope = (t: TFunction, { scope, valueType, value }: HoursRateContribution): string =>
	[
		...(scope.startTime && scope.endTime ? [`${clockOf(scope.startTime)}–${clockOf(scope.endTime)}`] : []),
		scope.roles.length > 0 ? compactList(t, scope.roles) : t("wageBreakdown.scope.allRoles"),
		...flagPart(t, scope.weekend, "wageBreakdown.scope.weekend", "wageBreakdown.scope.weekdays"),
		...flagPart(t, scope.isCharge, "wageBreakdown.scope.charge", "wageBreakdown.scope.nonCharge"),
		...listPart(t, scope.trainingStatuses),
		...listPart(t, scope.agency),
		...audienceParts(t, scope),
		// A flat rule's amount is the line's own figure; a percentage's is not, so it is named.
		...(valueType === RateHoursBonusType.Percentage ? [t("wageBreakdown.scope.percentAdded", { percent: signedPercentDelta(value - PERCENT_BASE) })] : []),
	].join(SCOPE_SEPARATOR);

export const breakDetail = (t: TFunction, c: BreakContribution): string =>
	[
		t("wageBreakdown.break.detail", { minutes: c.breakMinutes, hours: formatHoursNumber(c.appliesOverHours) }),
		...rolesPart(t, c.scope.roles),
		...audienceParts(t, c.scope),
	].join(SCOPE_SEPARATOR);

/** The shift's own mark that caused the replacement — named so the reader knows what to undo. */
const TRAINING_MARK: Readonly<Record<TrainingContribution["kind"], TranslationKeys>> = {
	training: "wageBreakdown.training.markedTrainee",
	orientation: "wageBreakdown.training.markedOrientation",
};

/**
 * "replaces base ($41.46 → $20.00) · shift marked Trainee", and for a percentage mark, what share of the
 * nurse's own wage it is.
 */
export const trainingDetail = (t: TFunction, c: TrainingContribution, own: string, replacement: string): string =>
	[
		t("wageBreakdown.training.replacesBase", { own, replacement }),
		...(c.valueType === RateHoursBonusType.Percentage ? [t("wageBreakdown.training.percentOfOwn", { percent: percentOf(c.value) })] : []),
		t(TRAINING_MARK[c.kind]),
	].join(SCOPE_SEPARATOR);
